import React from "react";
import { Tooltip, IconButton } from "@SeemplicityDev/shared";
import useFilterDetails from "../useFilterDetails";
import {
  FieldData,
  FilterItem,
  FilterPinItem,
} from "../../FilterManagement/CommonFilterTypes";
import {
  filterKeyEntityClassNames,
  filterKeyEntityLabels,
  splitFilterKey,
} from "../../FilterManagement/filterKey";
import useRemediationItemsSplit from "../../../General/useRemediationItemsSplit";
import "./FilterChip.scss";

export type FilterChipButtonProps = {
  filterItem?: FilterItem;
  isActive?: boolean;
  isBlink?: boolean;
  isUnsupported?: boolean;
  displayFullValue?: boolean;
  allowRemove?: boolean;
  handleRemoveFilter?: () => void;
  isEditable?: boolean;
  fieldsData: FieldData[];
  statusesData: any[];
  filtersToPin?: FilterPinItem[];
  onClick?: () => void;
  showEntity?: boolean;
};

export const FilterChipButton: React.FC<FilterChipButtonProps> = ({
  filterItem,
  isActive,
  isBlink,
  isUnsupported,
  onClick,
  displayFullValue,
  allowRemove,
  handleRemoveFilter,
  isEditable,
  fieldsData,
  statusesData,
  filtersToPin,
  showEntity,
}) => {
  const isRemediationItemsSplit = useRemediationItemsSplit();
  const { fieldData, conditionData, selectedValueData } = useFilterDetails(
    fieldsData,
    filterItem?.field,
    filterItem?.condition,
    filterItem?.value,
    statusesData,
    filtersToPin
  );

  const isEntityShown = showEntity ?? isRemediationItemsSplit;
  const { entity: keyEntity } = splitFilterKey(filterItem?.field);
  const entity = isEntityShown ? keyEntity : undefined;
  const fieldTooltip =
    entity && fieldData?.label
      ? `${filterKeyEntityLabels[entity]} · ${fieldData.label}`
      : fieldData?.label;

  const valueTooltip = () => {
    const val = displayFullValue
      ? selectedValueData?.title
      : selectedValueData?.label;

    if (
      typeof val === "object" &&
      val !== null &&
      "unit" in val &&
      "value" in val
    ) {
      const typedVal = val as { value: string | number; unit: string };
      return `${typedVal.value} ${typedVal.unit}`;
    }

    return val;
  };
  return (
    <div
      className={`filter-chip ${fieldData?.disabled || !isEditable ? "is-disabled" : ""} ${isActive ? "is-active" : ""} ${fieldData?.pinned ? "is-pinned" : ""} ${isBlink ? "is-blink" : ""} ${isUnsupported ? "is-unsupported" : ""} ${entity ? filterKeyEntityClassNames[entity] : ""}`}
      data-entity={entity}
      role={onClick ? "button" : "listitem"}
      onClick={() => isEditable && !fieldData?.disabled && onClick?.()}
      data-testid={`filter-chip-${filterItem?.field || "new-filter"}-container`}
    >
      {!!fieldData?.label ? (
        <>
          <div
            className={`filter-chip__field ${!!fieldData?.warning ? "has-warning" : ""}`}
            data-testid={"display-filter-chip-field"}
          >
            {fieldData?.pinned && (
              <Tooltip content={"Pinned filter"} type={"info"}>
                <i className={"seem-icon-pin pinned-filter"} />
              </Tooltip>
            )}
            {isUnsupported && (
              <Tooltip content={"Unsupported filter"}>
                <i className={"seem-icon seem-icon-warning unsupported-icon"} />
              </Tooltip>
            )}
            {!!fieldData?.warning && !isUnsupported && (
              <Tooltip content={fieldData.warning}>
                <i
                  className={`seem-icon seem-icon-warning deprecated-condition`}
                />
              </Tooltip>
            )}
            <Tooltip content={fieldTooltip}>
              <span>{fieldData?.label}</span>
            </Tooltip>
          </div>
          <Tooltip content={conditionData?.longLabel}>
            <div
              className={"filter-chip__condition"}
              data-testid={"display-filter-chip-condition"}
            >
              {conditionData?.deprecated && (
                <i
                  className={`seem-icon seem-icon-warning deprecated-condition`}
                />
              )}
              {conditionData?.label}
            </div>
          </Tooltip>
          <div
            className={`filter-chip__value ${displayFullValue ? "is-full-value" : ""}`}
          >
            <Tooltip content={selectedValueData?.title}>
              <span
                className={"value-text"}
                data-testid={"display-filter-chip-value"}
              >
                {valueTooltip()}
              </span>
            </Tooltip>
            {!fieldData?.pinned && isEditable && allowRemove && (
              <IconButton
                data-testid={"delete-condition-btn"}
                wrapperClassName={`remove-filter-button`}
                type="button"
                isIcon="seem-icon-close"
                size={"xs"}
                withTooltip={{ content: "Remove filter" }}
                onClick={handleRemoveFilter}
                startHidden={true}
              />
            )}
          </div>
        </>
      ) : (
        <div className={"filter-chip__new-filter"}>
          <span className="value-text">Add Filter</span>
          <IconButton
            data-testid={"delete-condition-btn"}
            wrapperClassName={`remove-filter-button`}
            type="button"
            isIcon="seem-icon-close"
            size={"xs"}
            withTooltip={{ content: "Remove filter" }}
            onClick={handleRemoveFilter}
            startHidden={true}
          />
        </div>
      )}
    </div>
  );
};

export default FilterChipButton;
